// Phase 6 · Planner.
// Monthly goal + four weekly themes + "approve the week" weekly slate.
// Owner-only approval, admin+owner can edit goals/themes, editors read-only.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// ---------- helpers ----------
function currentMonthKey() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function currentIsoWeek() {
  // ISO week number (Mon=1). Returns YYYY-Www.
  const d = new Date();
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((t - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function friendlyDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

const DEFAULT_THEMES = [
  { week_number: 1, name: "", description: "", target_post_count: 3 },
  { week_number: 2, name: "", description: "", target_post_count: 3 },
  { week_number: 3, name: "", description: "", target_post_count: 3 },
  { week_number: 4, name: "", description: "", target_post_count: 3 },
];

const LEVEL_LABEL = {
  manual: "Manual",
  draft_for_me: "Draft for me",
  auto_with_veto: "Auto with veto",
  fully_automatic: "Fully automatic",
};

// ---------- page ----------
export default function Planner() {
  const { role } = useAuth();
  const canEdit = role === "owner" || role === "admin";
  const canApprove = role === "owner";

  const [state, setState] = useState({ loading: true, error: null });
  const [month, setMonth] = useState(currentMonthKey());
  const [week, setWeek] = useState(currentIsoWeek());
  const [goal, setGoal] = useState({ goal: "", north_star_metric: "", target_value: "" });
  const [themes, setThemes] = useState(DEFAULT_THEMES);
  const [slate, setSlate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [approveNote, setApproveNote] = useState("");

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [g, t, s] = await Promise.all([
        api.get(`/planner/monthly-goal?month=${encodeURIComponent(month)}`),
        api.get(`/planner/weekly-themes?month=${encodeURIComponent(month)}`),
        api.get(`/planner/weekly-slate?week=${encodeURIComponent(week)}`),
      ]);
      const existingGoal = g.data?.goal || null;
      setGoal({
        goal: existingGoal?.goal || "",
        north_star_metric: existingGoal?.north_star_metric || "",
        target_value: existingGoal?.target_value ?? "",
      });
      const incoming = (t.data?.themes || []);
      if (incoming.length) {
        // Pad to 4 slots so UI is always consistent.
        const map = new Map(incoming.map((th) => [th.week_number, th]));
        setThemes(DEFAULT_THEMES.map((d) => map.get(d.week_number) || d));
      } else {
        setThemes(DEFAULT_THEMES);
      }
      setSlate(s.data || null);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [month, week]);

  useEffect(() => { load(); }, [load]);

  async function saveGoal() {
    if (!canEdit) return;
    setBusy(true);
    try {
      const payload = {
        month,
        goal: goal.goal,
        north_star_metric: goal.north_star_metric || null,
        target_value: goal.target_value === "" ? null : Number(goal.target_value),
      };
      await api.put("/planner/monthly-goal", payload);
      setFlash("Monthly goal saved · audit row written.");
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setBusy(false);
    }
  }

  async function saveThemes() {
    if (!canEdit) return;
    setBusy(true);
    try {
      const cleaned = themes
        .filter((t) => (t.name || "").trim())
        .map((t) => ({
          week_number: t.week_number,
          name: t.name.trim(),
          description: (t.description || "").trim() || null,
          target_post_count: Number(t.target_post_count) || 0,
        }));
      await api.put("/planner/weekly-themes", { month, themes: cleaned });
      setFlash("Weekly themes saved · audit row written.");
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setBusy(false);
    }
  }

  async function approveWeek() {
    if (!canApprove) return;
    setBusy(true);
    try {
      await api.post("/planner/weekly-slate/approve", {
        week,
        note: approveNote || null,
      });
      setApproveNote("");
      setFlash("Week approved · audit row written.");
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setBusy(false);
    }
  }

  const weekLabel = useMemo(() => {
    if (!slate?.monday) return week;
    try {
      return `${new Date(slate.monday).toLocaleDateString("en-US", {
        timeZone: "America/New_York", month: "short", day: "numeric",
      })} — ${new Date(slate.next_monday).toLocaleDateString("en-US", {
        timeZone: "America/New_York", month: "short", day: "numeric",
      })}`;
    } catch { return week; }
  }, [slate, week]);

  if (state.loading) {
    return (
      <Shell title="Planner">
        <LoadingCard label="Loading planner…" />
      </Shell>
    );
  }

  const planCount = slate?.plan_count || 0;
  const posting = slate?.autonomy?.posting;
  const paused = slate?.autonomy?.pause_everything;

  return (
    <Shell title="Planner">
      <div className="bo-page" data-testid="planner-page">
        {/* Header */}
        <section className="bo-card">
          <div className="bo-eyebrow">Phase 6 · Planner</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Monthly goal · weekly themes · approve the week
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 780, lineHeight: 1.55 }}>
            One North-Star goal per month, four weekly themes, and a Monday
            slate you approve as a whole. Only the owner can approve a week.
            Every save writes to the audit log.
          </p>
          <ErrorBanner error={state.error} />
          {flash && (
            <div className="bo-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)" }} data-testid="planner-flash">
              {flash}
            </div>
          )}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              Month:
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value || currentMonthKey())}
                style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                data-testid="planner-month-input"
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              Week:
              <input
                type="week"
                value={week}
                onChange={(e) => setWeek(e.target.value || currentIsoWeek())}
                style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                data-testid="planner-week-input"
              />
            </label>
            <Link to="/brand-os/governance" className="bo-btn bo-btn-ghost" data-testid="planner-audit-link">
              View audit log →
            </Link>
          </div>
        </section>

        {/* Monthly Goal */}
        <section className="bo-card" style={{ marginTop: 16 }} data-testid="planner-monthly-goal">
          <div className="bo-eyebrow">Monthly goal · {month}</div>
          <h2 className="bo-serif" style={{ fontSize: 20, margin: "4px 0 10px" }}>
            What's the North Star for this month?
          </h2>
          <div style={{ display: "grid", gap: 10, maxWidth: 760 }}>
            <textarea
              rows={2}
              value={goal.goal}
              disabled={!canEdit || busy}
              onChange={(e) => setGoal({ ...goal, goal: e.target.value })}
              placeholder="e.g. Add 200 Founding Families to the Well waitlist."
              style={{ padding: "10px 12px", borderRadius: 6, border: "1px solid var(--bo-border)", fontSize: 14, lineHeight: 1.5 }}
              data-testid="planner-goal-text"
            />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                Metric:
                <input
                  type="text"
                  value={goal.north_star_metric}
                  disabled={!canEdit || busy}
                  onChange={(e) => setGoal({ ...goal, north_star_metric: e.target.value })}
                  placeholder="waitlist_signups"
                  style={{ width: 200, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                  data-testid="planner-goal-metric"
                />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                Target:
                <input
                  type="number"
                  value={goal.target_value}
                  disabled={!canEdit || busy}
                  onChange={(e) => setGoal({ ...goal, target_value: e.target.value })}
                  placeholder="200"
                  style={{ width: 110, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                  data-testid="planner-goal-target"
                />
              </label>
              {canEdit ? (
                <button
                  className="bo-btn bo-btn-primary"
                  onClick={saveGoal}
                  disabled={busy || !goal.goal.trim()}
                  data-testid="planner-goal-save"
                >
                  Save monthly goal
                </button>
              ) : (
                <Pill tone="muted">Read-only (editors cannot change goals)</Pill>
              )}
            </div>
          </div>
          {!goal.goal.trim() && (
            <div style={{ marginTop: 10 }}>
              <EmptyState
                title="No goal set for this month yet."
                description={canEdit
                  ? "Write one sentence and click Save. The weekly themes below should ladder up to it."
                  : "Only owners and admins can set the monthly goal."}
                testId="planner-goal-empty"
              />
            </div>
          )}
        </section>

        {/* Weekly Themes */}
        <section className="bo-card" style={{ marginTop: 16 }} data-testid="planner-weekly-themes">
          <div className="bo-eyebrow">Weekly themes · {month}</div>
          <h2 className="bo-serif" style={{ fontSize: 20, margin: "4px 0 10px" }}>
            Four themes, one per week
          </h2>
          <div style={{ display: "grid", gap: 10 }}>
            {themes.map((th, i) => (
              <div
                key={th.week_number}
                style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start",
                         padding: 10, borderRadius: 8, background: "var(--bo-panel-alt)" }}
                data-testid={`planner-theme-week-${th.week_number}`}
              >
                <div style={{ fontWeight: 600, minWidth: 60, paddingTop: 8 }}>
                  Week {th.week_number}
                </div>
                <input
                  type="text"
                  value={th.name}
                  disabled={!canEdit || busy}
                  onChange={(e) => {
                    const next = [...themes];
                    next[i] = { ...th, name: e.target.value };
                    setThemes(next);
                  }}
                  placeholder="Theme name"
                  style={{ flex: "1 1 220px", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--bo-border)", fontSize: 14 }}
                  data-testid={`planner-theme-name-${th.week_number}`}
                />
                <input
                  type="text"
                  value={th.description || ""}
                  disabled={!canEdit || busy}
                  onChange={(e) => {
                    const next = [...themes];
                    next[i] = { ...th, description: e.target.value };
                    setThemes(next);
                  }}
                  placeholder="One-line description (optional)"
                  style={{ flex: "2 1 320px", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--bo-border)", fontSize: 14 }}
                  data-testid={`planner-theme-desc-${th.week_number}`}
                />
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  Posts:
                  <input
                    type="number"
                    min={0}
                    max={21}
                    value={th.target_post_count}
                    disabled={!canEdit || busy}
                    onChange={(e) => {
                      const next = [...themes];
                      next[i] = { ...th, target_post_count: Number(e.target.value) || 0 };
                      setThemes(next);
                    }}
                    style={{ width: 60, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                    data-testid={`planner-theme-count-${th.week_number}`}
                  />
                </label>
              </div>
            ))}
          </div>
          {canEdit && (
            <div style={{ marginTop: 12 }}>
              <button
                className="bo-btn bo-btn-primary"
                onClick={saveThemes}
                disabled={busy}
                data-testid="planner-themes-save"
              >
                Save weekly themes
              </button>
            </div>
          )}
        </section>

        {/* Weekly Slate / Approve the Week */}
        <section className="bo-card" style={{ marginTop: 16 }} data-testid="planner-weekly-slate">
          <div className="bo-eyebrow">Weekly slate · {week}</div>
          <h2 className="bo-serif" style={{ fontSize: 20, margin: "4px 0 10px" }}>
            Approve this week — {weekLabel}
          </h2>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {paused && <Pill tone="danger">Pause everything is ON</Pill>}
            {slate?.monthly_goal?.goal
              ? <Pill tone="success">Monthly goal set</Pill>
              : <Pill tone="warn">No monthly goal</Pill>}
            {slate?.weekly_theme?.name
              ? <Pill tone="success">Theme: {slate.weekly_theme.name}</Pill>
              : <Pill tone="warn">No weekly theme</Pill>}
            {posting && <Pill tone="muted">Posting: {LEVEL_LABEL[posting] || posting}</Pill>}
            <Pill tone="muted">Veto window: {slate?.autonomy?.veto_window_hours ?? 24}h</Pill>
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>
              Posts in this week: {planCount}
            </div>
            {planCount === 0 ? (
              <EmptyState
                title="No posts scheduled for this week."
                description="Draft posts for this week in Creative Studio or the Publish queue. They'll appear here once scheduled inside this ISO week window."
                testId="planner-slate-empty"
              />
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {(slate?.publish_plans || []).map((p) => (
                  <div
                    key={p.id}
                    style={{ display: "flex", gap: 10, alignItems: "center",
                             padding: 8, borderRadius: 6, background: "var(--bo-panel-alt)" }}
                    data-testid={`planner-slate-item-${p.id}`}
                  >
                    <Pill tone="muted">{p.channel}</Pill>
                    <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 13, color: "var(--bo-muted)" }}>
                      {friendlyDate(p.scheduled_at)}
                    </div>
                    <div style={{ flex: 1, fontSize: 14 }}>
                      {p.title || <span style={{ color: "var(--bo-muted)" }}>(no title yet)</span>}
                    </div>
                    <Pill tone={p.state === "scheduled" ? "success" : p.state === "preflight_ok" ? "muted" : "warn"}>
                      {p.state}
                    </Pill>
                  </div>
                ))}
              </div>
            )}
          </div>

          {canApprove ? (
            <div style={{ marginTop: 14, display: "grid", gap: 10, maxWidth: 760 }}>
              <input
                type="text"
                placeholder="Approval note (optional, logged)"
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--bo-border)", fontSize: 14 }}
                data-testid="planner-approve-note"
              />
              <div>
                <button
                  className="bo-btn bo-btn-primary"
                  onClick={approveWeek}
                  disabled={busy}
                  data-testid="planner-approve-btn"
                >
                  Approve this week
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <Pill tone="muted">Only the owner may approve a week.</Pill>
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
}
